import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { fetchFastApi } from "../../api/fastapi";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const queryEmail = searchParams.get("email");
  const [email, setEmail] = useState(queryEmail || "");
  const [emailSent, setEmailSent] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [error, setError] = useState("");

  const sendResetEmail = useCallback(async () => {
    try {
      await fetchFastApi({
        body: { email },
        method: "POST",
        path: "/auth/reset-password/initiate",
      });
      setEmailSent(true);
    } catch (e) {
      console.error("Error during email sign-in:", e);
    }
  }, [email]);

  const resetPassword = useCallback(async () => {
    try {
      const res = await fetchFastApi({
        body: { email, code: resetCode, new_password: newPassword },
        method: "POST",
        path: "/auth/reset-password/confirm",
      });
      if (res.error_message) {
        setError(res.error_message);
        return;
      }

      setIsPasswordReset(true);
    } catch (e) {
      console.error("error during reset password", e);
    }
  }, [email, resetCode, newPassword]);

  const children = useMemo(() => {
    if (isPasswordReset) {
      return (
        <div className="flex flex-col gap-4">
          <div className="text-font-secondary">Password reset successfully</div>
          <Link className="text-sm underline" to="/signin">
            Go to sign in
          </Link>
        </div>
      );
    }
    if (emailSent) {
      return (
        <div className="flex flex-col gap-4">
          <div className="text-font-secondary">
            Please check your email for a link to reset your password.
          </div>
          <Input
            autoComplete="one-time-code"
            type="resetCode"
            id="resetCode"
            placeholder="Reset Code"
            onChange={(e) => setResetCode(e.target.value)}
            value={resetCode}
          />
          <Input
            autoComplete="new-password"
            type="password"
            id="newPassword"
            placeholder="New Password"
            onChange={(e) => setNewPassword(e.target.value)}
            value={newPassword}
          />
          <Button className="mt-2" onClick={resetPassword}>
            Reset Password
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="text-sm font-semibold">
          Send Reset Email
        </label>
        <Input
          autoComplete="email"
          type="email"
          id="email"
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
        />
        <Button className="mt-2" onClick={sendResetEmail}>
          Reset Password
        </Button>
      </div>
    );
  }, [
    email,
    setEmail,
    emailSent,
    isPasswordReset,
    resetCode,
    newPassword,
    setResetCode,
    setNewPassword,
    sendResetEmail,
    resetPassword,
  ]);

  return (
    <div className="h-screen w-screen bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
      <div className="flex h-screen justify-center items-center">
        <div className="bg-white p-8 rounded-sm shadow-2xl z-10 w-96 border border-stone-100">
          <div className="flex flex-col gap-4">
            <img src="/logo_white_bg.svg" alt="logo" className="w-20" />
            <div>
              <div
                data-testid="reset-password-heading"
                className="text-2xl font-semibold"
              >
                Reset Password
              </div>
            </div>
            {children}
            <div
              data-testid="reset-password-error"
              className="text-sm text-red-500"
            >
              {error}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
